type MessageHandler = (msg: WSMessage) => void;

export interface WSMessage {
  type: string;
  [key: string]: unknown;
}

const WS_URL = import.meta.env.VITE_WS_URL || '';

class ShipditWebSocket {
  private ws: WebSocket | null = null;
  private handlers = new Set<MessageHandler>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private boardIds: string[] = [];

  /**
   * Open a WS connection and subscribe to one or more boards.
   * Back-compat: accepts a single boardId string.
   */
  connect(boardIds?: string | string[]) {
    if (!WS_URL) return;
    this.boardIds = normalizeBoardIds(boardIds);
    this.close();

    try {
      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        console.log('[WS] connected');
        for (const id of this.boardIds) {
          this.send({ action: 'subscribe', boardId: id });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WSMessage;
          this.handlers.forEach((h) => h(msg));
        } catch {
          // ignore non-JSON messages
        }
      };

      this.ws.onclose = () => {
        console.log('[WS] disconnected');
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        this.ws?.close();
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  /** Replace the set of subscribed boards on an already-open connection.
   *  Sends subscribe/unsubscribe deltas; reconnects if no socket is open. */
  setBoards(boardIds: string[]) {
    const next = normalizeBoardIds(boardIds);
    const prev = new Set(this.boardIds);
    const nextSet = new Set(next);

    this.boardIds = next;

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connect(next);
      return;
    }

    for (const id of next) {
      if (!prev.has(id)) this.send({ action: 'subscribe', boardId: id });
    }
    for (const id of prev) {
      if (!nextSet.has(id)) this.send({ action: 'unsubscribe', boardId: id });
    }
  }

  close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  send(data: Record<string, unknown>) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  subscribe(handler: MessageHandler) {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  subscribeLogs() {
    this.send({ action: 'subscribe-logs' });
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.boardIds);
    }, 3000);
  }
}

function normalizeBoardIds(input?: string | string[]): string[] {
  if (!input) return [];
  if (typeof input === 'string') return [input];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of input) {
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

export const ws = new ShipditWebSocket();
